import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bot,
  ChevronDown,
  FileText,
  Globe,
  Image as ImageIcon,
  Instagram,
  LayoutDashboard,
  LogOut,
  Mail,
  MapPinned,
  Power,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import { useAuth } from "../../auth/AuthProvider";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";

function getPageMeta(
  pathname: string,
  pick: (ja: string, en: string) => string,
): { title: string; crumb: string[] } {
  const pageMeta: Record<string, { title: string; crumb: string[] }> = {
    "/dashboard": { title: pick("ダッシュボード", "Dashboard"), crumb: [pick("ホーム", "Home"), pick("ダッシュボード", "Dashboard")] },
    "/site": { title: pick("サイト管理", "Sites"), crumb: [pick("ホーム", "Home"), pick("サイト管理", "Sites")] },
    "/blog": { title: pick("ブログ管理", "Blog"), crumb: [pick("ホーム", "Home"), pick("ブログ管理", "Blog")] },
    "/ai-blog": { title: pick("AIブログ生成", "AI Blog"), crumb: [pick("ホーム", "Home"), pick("AIブログ生成", "AI Blog")] },
    "/chatbot-demo": {
      title: pick("チャットボット(デモ)", "Chatbot (Demo)"),
      crumb: [pick("ホーム", "Home"), pick("チャットボット", "Chatbot"), pick("デモ", "Demo")],
    },
    "/chatbot": { title: pick("チャットボット", "Chatbot"), crumb: [pick("ホーム", "Home"), pick("チャットボット", "Chatbot")] },
    "/analytics": { title: pick("アクセス解析", "Analytics"), crumb: [pick("ホーム", "Home"), pick("アクセス解析", "Analytics")] },
    "/google-business": {
      title: pick("Googleビジネスプロフィール", "Google Business Profile"),
      crumb: [pick("ホーム", "Home"), pick("Googleビジネス", "Google Business")],
    },
    "/mail": { title: pick("メール対応", "Mail"), crumb: [pick("ホーム", "Home"), pick("メール対応", "Mail")] },
    "/mail-v2": { title: pick("メール対応", "Mail"), crumb: [pick("ホーム", "Home"), pick("メール対応", "Mail")] },
    "/media": { title: pick("メディアライブラリ", "Media Library"), crumb: [pick("ホーム", "Home"), pick("メディア", "Media")] },
    "/instagram-v2": { title: "Instagram Management", crumb: [pick("ホーム", "Home"), "Instagram Management"] },
    "/media-demo": {
      title: pick("メディアライブラリ(デモ)", "Media Library (Demo)"),
      crumb: [pick("ホーム", "Home"), pick("メディア", "Media"), pick("デモ", "Demo")],
    },
    "/users": { title: pick("ユーザー管理", "Users"), crumb: [pick("ホーム", "Home"), pick("管理", "Admin"), pick("ユーザー", "Users")] },
    "/settings": { title: pick("システム設定", "System Settings"), crumb: [pick("ホーム", "Home"), pick("管理", "Admin"), pick("設定", "Settings")] },
  };

  return pageMeta[pathname] ?? {
    title: pick("ダッシュボード", "Dashboard"),
    crumb: [pick("ホーム", "Home")],
  };
}

/**
 * ヘッダー
 * - ページタイトル + パンくず
 * - 顧客切替 / 通知 / ユーザ
 */
export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, logoutAll } = useAuth();
  const { pick } = useUiLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const meta = getPageMeta(location.pathname, pick);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  async function handleLogoutAll() {
    if (!window.confirm(pick("すべてのデバイスからログアウトします。よろしいですか?", "Log out from all devices?"))) return;
    setMenuOpen(false);
    await logoutAll();
    navigate("/login", { replace: true });
  }

  const initial = user?.name ? user.name.trim().charAt(0) : "?";

  const headerIcon = (() => {
    switch (location.pathname) {
      case "/dashboard":
        return { icon: <LayoutDashboard size={18} />, className: "dashboard" };
      case "/site":
        return { icon: <Globe size={18} />, className: "site" };
      case "/chatbot":
      case "/chatbot-demo":
        return { icon: <Bot size={18} />, className: "chatbot" };
      case "/analytics":
        return { icon: <BarChart3 size={18} />, className: "analytics" };
      case "/google-business":
        return { icon: <MapPinned size={18} />, className: "google-business" };
      case "/mail":
      case "/mail-v2":
        return { icon: <Mail size={18} />, className: "mail" };
      case "/instagram-v2":
        return { icon: <Instagram size={18} />, className: "instagram" };
      case "/media":
      case "/media-demo":
        return { icon: <ImageIcon size={18} />, className: "media" };
      case "/blog":
        return { icon: <FileText size={18} />, className: "blog" };
      case "/ai-blog":
        return { icon: <Sparkles size={18} />, className: "ai-blog" };
      case "/users":
        return { icon: <Users size={18} />, className: "users" };
      case "/settings":
        return { icon: <Settings size={18} />, className: "settings" };
      default:
        return null;
    }
  })();

  return (
    <header className={`header ${location.pathname === "/instagram-v2" ? "header--instagram" : ""}`}>
      <div className="header-left">
        <div className="header-title-row">
          {headerIcon ? (
            <span className={`header-title-icon header-title-icon--${headerIcon.className}`} aria-hidden="true">
              {headerIcon.icon}
            </span>
          ) : null}
          <h1 className="header-title">{meta.title}</h1>
        </div>
      </div>

      <div className="header-right">
        <div className="header-user-wrapper" ref={menuRef}>
          <div
            className="header-user"
            onClick={() => setMenuOpen((v) => !v)}
            role="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <div className="header-user-avatar">{initial}</div>
            <span className="header-user-name">{user?.name ?? ""}</span>
            <ChevronDown size={14} color="#6b7280" />
          </div>

          {menuOpen && (
            <div className="header-user-menu" role="menu">
              <div className="header-user-menu-info">
                <div className="header-user-menu-email">{user?.email}</div>
              </div>
              <button
                type="button"
                className="header-user-menu-item"
                onClick={handleLogout}
              >
                <LogOut size={14} />
                <span>{pick("ログアウト(このデバイス)", "Log out (this device)")}</span>
              </button>
              <button
                type="button"
                className="header-user-menu-item header-user-menu-item-danger"
                onClick={handleLogoutAll}
              >
                <Power size={14} />
                <span>{pick("すべてのデバイスからログアウト", "Log out from all devices")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
